import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Download, Eye, FileText } from 'lucide-react';
import PDFViewer from '../components/PDFViewer';
import newsletter202501 from '../assets/newsletters/1. JAJ January 2025 .pdf';
import newsletter202502 from '../assets/newsletters/2.JAJ February 2025 Newsletter.pdf';
import newsletter202503 from '../assets/newsletters/3.JAJ March 2025.pdf';
import newsletter202504 from '../assets/newsletters/4. JAJ April 2025.pdf';
import newsletter202505 from '../assets/newsletters/5. JAJ May 2025.pdf';
import newsletter202506 from '../assets/newsletters/6. JAJ June 2025.pdf';
import newsletter202507 from '../assets/newsletters/7. JAJ July 2025 - Google ドキュメント.pdf';
import newsletter202508 from '../assets/newsletters/8. JAJ August 2025 .pdf';
import newsletter202509 from '../assets/newsletters/9. JAJ September 2025 Newsletter .docx.pdf';
import newsletter202511 from '../assets/newsletters/11. JAJ November 2025 Newsletter .pdf';
import newsletter202602 from '../assets/newsletters/2. JAJ Newsletter (Feb 2026).pdf';
import newsletter202603 from '../assets/newsletters/3. JAJ Newsletter (Mar 2026) .docx.pdf';
import newsletter202604 from '../assets/newsletters/4. JAJ Newsletter (Apr 2026).pdf';
import newsletter202605 from '../assets/newsletters/5. JAJ Newsletter (May 2026).pdf';
import newsletter202607 from '../assets/newsletters/JAJ July 2026 Newsletter.pdf';
import newsletter202608 from '../assets/newsletters/8. JAJ Newsletter (August 2026).pdf';

interface NewsletterItem {
  id: string;
  title: string;
  date: string;
  description: string;
  fileName: string;
  assetUrl: string;
  localUrl: string;
  downloadUrl: string;
}

const newsletterData = [
  {
    id: '2026-08',
    date: '2026-08-01',
    fileName: '8. JAJ Newsletter (August 2026).pdf',
    assetUrl: newsletter202608,
  },
  {
    id: '2026-07',
    date: '2026-07-01',
    fileName: 'JAJ July 2026 Newsletter.pdf',
    assetUrl: newsletter202607,
  },
  {
    id: '2026-05',
    date: '2026-05-01',
    fileName: '5. JAJ Newsletter (May 2026).pdf',
    assetUrl: newsletter202605,
  },
  {
    id: '2026-04',
    date: '2026-04-01',
    fileName: '4. JAJ Newsletter (Apr 2026).pdf',
    assetUrl: newsletter202604,
  },
  {
    id: '2026-03',
    date: '2026-03-01',
    fileName: '3. JAJ Newsletter (Mar 2026) .docx.pdf',
    assetUrl: newsletter202603,
  },
  {
    id: '2026-02',
    date: '2026-02-01',
    fileName: '2. JAJ Newsletter (Feb 2026).pdf',
    assetUrl: newsletter202602,
  },
  {
    id: '2025-11',
    date: '2025-11-01',
    fileName: '11. JAJ November 2025 Newsletter .pdf',
    assetUrl: newsletter202511,
  },
  {
    id: '2025-09',
    date: '2025-09-01',
    fileName: '9. JAJ September 2025 Newsletter .docx.pdf',
    assetUrl: newsletter202509,
  },
  {
    id: '2025-08',
    date: '2025-08-01',
    fileName: '8. JAJ August 2025 .pdf',
    assetUrl: newsletter202508,
  },
  {
    id: '2025-07',
    date: '2025-07-01',
    fileName: '7. JAJ July 2025 - Google ドキュメント.pdf',
    assetUrl: newsletter202507,
  },
  {
    id: '2025-06',
    date: '2025-06-01',
    fileName: '6. JAJ June 2025.pdf',
    assetUrl: newsletter202506,
  },
  {
    id: '2025-05',
    date: '2025-05-01',
    fileName: '5. JAJ May 2025.pdf',
    assetUrl: newsletter202505,
  },
  {
    id: '2025-04',
    date: '2025-04-01',
    fileName: '4. JAJ April 2025.pdf',
    assetUrl: newsletter202504,
  },
  {
    id: '2025-03',
    date: '2025-03-01',
    fileName: '3.JAJ March 2025.pdf',
    assetUrl: newsletter202503,
  },
  {
    id: '2025-02',
    date: '2025-02-01',
    fileName: '2.JAJ February 2025 Newsletter.pdf',
    assetUrl: newsletter202502,
  },
  {
    id: '2025-01',
    date: '2025-01-01',
    fileName: '1. JAJ January 2025 .pdf',
    assetUrl: newsletter202501,
  }
];

const Newsletter: React.FC = () => {
  const { t } = useTranslation();
  const [selectedPDF, setSelectedPDF] = useState<NewsletterItem | null>(null);

  // Actual newsletter data based on the PDF files in the directory
  const newsletters = useMemo<NewsletterItem[]>(() => {
    return newsletterData.map(item => ({
      ...item,
      title: t(`newsletter.newsletters.${item.id}.title`),
      description: t(`newsletter.newsletters.${item.id}.description`),
      localUrl: item.assetUrl,
      downloadUrl: item.assetUrl
    }));
  }, [t]);

  // Sort newsletters in descending order by date
  const sortedNewsletters = useMemo(() => {
    return [...newsletters].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [newsletters]);

  const handleDownload = (newsletter: NewsletterItem) => {
    const link = document.createElement('a');
    link.href = newsletter.downloadUrl;
    link.download = newsletter.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = (newsletter: NewsletterItem) => {
    setSelectedPDF(newsletter);
  };

  const closePDFViewer = () => {
    setSelectedPDF(null);
  };

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(new Date(`${date}T12:00:00`));

  return (
    <>
      <section className="page-section">
        <div className="page-container">
          <div className="page-intro">
            <p className="eyebrow">Stories from JAJ</p>
            <h1 className="page-title">{t('newsletter.title')}</h1>
            <p className="page-description">Browse community news, event recaps, member stories, and cultural notes from the JAJ archive.</p>
          </div>
          <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div>

              {/* Newsletter Archive */}
              <div className="surface p-3 sm:p-5">
                {/* <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  {t('newsletter.archive.title')}
                </h2> */}
                <div className="divide-y divide-[#e3e7ec]">
                  {sortedNewsletters.map((newsletter) => (
                    <div 
                      key={newsletter.id}
                      className="group grid gap-4 px-3 py-5 transition hover:bg-[#f8fafc] sm:grid-cols-[64px_minmax(0,1fr)_auto] sm:items-center sm:px-4"
                    >
                      <div className="hidden h-14 w-14 items-center justify-center bg-[#edf2f8] text-[#2457a7] sm:flex">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 pr-2">
                            {newsletter.title}
                          </h3>
                          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#d95f75]">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(newsletter.date)}
                          </p>
                          <p className="overflow-hidden text-xs leading-relaxed text-[#667085] sm:text-sm">
                            <span>{newsletter.description}</span>
                          </p>
                      </div>
                        <div className="flex gap-2 sm:flex-none">
                          <button
                            onClick={() => handlePreview(newsletter)}
                            className="icon-button"
                            title={t('newsletter.actions.view')}
                            aria-label={`${t('newsletter.actions.view')}: ${newsletter.title}`}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(newsletter)}
                            className="icon-button"
                            title={t('newsletter.actions.download')}
                            aria-label={`${t('newsletter.actions.download')}: ${newsletter.title}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <aside className="h-fit bg-[#172033] p-6 text-white sm:p-8 lg:sticky lg:top-28">
              <h2 className="mb-3 text-xl font-semibold text-white sm:mb-4 sm:text-2xl">
                {t('newsletter.info.title')}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                <p className="text-sm text-white/70 sm:text-base">
                  {t('newsletter.info.frequency')}
                </p>
                <ul className="space-y-2 text-sm text-white/70 sm:text-base">
                  <li className="flex items-start">
                    <span className="mr-2 text-[#f4b7c2]">•</span>
                    {t('newsletter.info.content.events')}
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#f4b7c2]">•</span>
                    {t('newsletter.info.content.news')}
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#f4b7c2]">•</span>
                    {t('newsletter.info.content.culture')}
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#f4b7c2]">•</span>
                    {t('newsletter.info.content.members')}
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 text-[#f4b7c2]">•</span>
                    {t('newsletter.info.content.announcements')}
                  </li>
                </ul>
                <div className="mt-6 border border-white/15 bg-white/5 p-4">
                  <h3 className="mb-2 text-sm font-semibold sm:text-base">{t('newsletter.latest.title')}</h3>
                  <p className="text-xs text-white/60 sm:text-sm">
                    {sortedNewsletters.length > 0 ? sortedNewsletters[0].title : t('newsletter.fallback.noNewsletters')}
                  </p>
                  {/* <p className="text-xs text-gray-500 mt-1">
                    {sortedNewsletters.length > 0 ? formatDate(sortedNewsletters[0].date) : ''}
                  </p> */}
                  {sortedNewsletters.length > 0 && (
                    <div className="mt-4 flex flex-col gap-2">
                      <button
                        onClick={() => handlePreview(sortedNewsletters[0])}
                        className="btn-primary w-full bg-white text-[#172033] hover:bg-[#f4d5da]"
                      >
                        {t('newsletter.latest.viewButton')}
                      </button>
                      <button
                        onClick={() => handleDownload(sortedNewsletters[0])}
                        className="btn-secondary w-full border-white/25 bg-transparent text-white hover:border-white hover:text-white"
                      >
                        {t('newsletter.latest.downloadButton')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* PDF Viewer Modal */}
      {selectedPDF && (
        <PDFViewer
          isOpen={!!selectedPDF}
          onClose={closePDFViewer}
          localUrl={selectedPDF.localUrl}
          title={selectedPDF.title}
        />
      )}
    </>
  );
};

export default Newsletter; 
